/**
 * Quick ROI — collapsed = snapshot cards from live calc state;
 * expanded = full Cost Price calculator. Never both at once.
 *
 * Cost semantics:
 * - null / unknown → display "—"; ROI / Est. Profit unavailable
 * - explicit 0 → display "$0.00" (only when user entered zero)
 * - never coerce unknown → 0 via Number(x)||0
 *
 * Buy Sources costs are NOT auto-wired (manual entry remains the design —
 * heading copy: "Enter your buy cost to preview profitability").
 */
import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

function money(value) {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "—";
  return `$${Number(value).toFixed(2)}`;
}

function pct(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return `${Number(value).toFixed(1)}%`;
}

/** True only when the operator has entered a finite numeric cost (incl. 0). */
export function isKnownCost(cost) {
  if (cost === null || cost === undefined || cost === "") return false;
  return Number.isFinite(Number(cost));
}

/**
 * ROI / Est. Profit require a known acquisition cost.
 * Net Payout / Break-even (sale − fees) do not require cost.
 */
export function computeQuickRoi({
  cost,
  sale,
  referralRate,
  fulfillment,
  wfsFee,
  sellerFee,
}) {
  const saleNum = Number(sale);
  const saleOk = Number.isFinite(saleNum) && saleNum > 0;
  const fee =
    fulfillment === "wfs"
      ? Number(wfsFee ?? 0.55)
      : Number(sellerFee ?? 0.35);
  const rate = Number(referralRate ?? 0.15);
  const referral = saleOk ? saleNum * rate : null;
  const netPayout =
    saleOk && referral != null && Number.isFinite(fee) ? saleNum - referral - fee : null;

  if (!isKnownCost(cost)) {
    return {
      costKnown: false,
      profit: null,
      roi: null,
      margin: null,
      netPayout,
      breakEven: netPayout,
    };
  }

  const costNum = Number(cost);
  const profit = netPayout != null ? netPayout - costNum : null;
  const roi = costNum > 0 && profit != null ? (profit / costNum) * 100 : null;
  const margin = saleOk && profit != null ? (profit / saleNum) * 100 : null;
  return {
    costKnown: true,
    profit,
    roi,
    margin,
    netPayout,
    breakEven: netPayout,
  };
}

export function QuickROI({ product, salePrice = null, visible = true }) {
  const saleRaw = salePrice ?? product?.buy_box_price ?? product?.storefront_price;
  const sale = Number(saleRaw);
  const referralRate = Number(product?.referral_rate ?? 0.15);
  const defaultFulfillment =
    String(product?.fulfillment || "").toUpperCase() === "WFS" ? "wfs" : "seller";

  const [open, setOpen] = useState(false);
  const [fulfillment, setFulfillment] = useState(defaultFulfillment);
  /** Unknown until the operator enters a cost — never default to $0.00. */
  const [cost, setCost] = useState(null);

  const calc = useMemo(
    () =>
      computeQuickRoi({
        cost,
        sale,
        referralRate,
        fulfillment,
        wfsFee: product?.wfs_fee,
        sellerFee: product?.seller_fee,
      }),
    [cost, fulfillment, product, referralRate, sale]
  );

  if (!visible) return null;

  const step = (dir) => {
    setCost((c) => {
      const base = isKnownCost(c) ? Number(c) : 0;
      return Math.max(0, Number((base + (dir === "up" ? 0.01 : -0.01)).toFixed(2)));
    });
  };

  const snapshots = [
    {
      key: "cost",
      label: "Cost Price",
      value: isKnownCost(cost) ? money(Number(cost)) : "—",
    },
    {
      key: "profit",
      label: "Est. Profit",
      value: calc.profit == null ? "—" : money(calc.profit),
      positive: calc.profit != null && calc.profit > 0,
    },
    {
      key: "roi",
      label: "ROI",
      value: calc.roi == null ? "—" : pct(calc.roi),
      positive: calc.roi != null && calc.roi > 0,
    },
    {
      key: "breakEven",
      label: "Break-even",
      value: calc.breakEven == null ? "—" : money(calc.breakEven),
    },
    {
      key: "netPayout",
      label: "Net Payout",
      value: calc.netPayout == null ? "—" : money(calc.netPayout),
    },
  ];

  return (
    <section
      className={`sfh-quick-roi${open ? " is-open" : ""}`}
      data-testid="sfh-quick-roi"
    >
      <div className="sfh-roi-heading">
        <div className="sfh-roi-heading-copy">
          <strong>Quick ROI</strong>
          <span>Enter your buy cost to preview profitability</span>
        </div>
        <div className="sfh-roi-heading-actions">
          <div className="sfh-fulfillment" data-testid="sfh-fulfillment">
            <button
              type="button"
              className={fulfillment === "wfs" ? "active" : ""}
              onClick={() => setFulfillment("wfs")}
            >
              WFS
            </button>
            <button
              type="button"
              className={fulfillment === "seller" ? "active" : ""}
              onClick={() => setFulfillment("seller")}
            >
              Seller Fulfilled
            </button>
          </div>
          <button
            type="button"
            className="sfh-roi-collapse"
            aria-expanded={open}
            aria-label={open ? "Collapse Quick ROI" : "Expand Quick ROI"}
            onClick={() => setOpen((v) => !v)}
            data-testid="sfh-quick-roi-toggle"
          >
            {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      {!open ? (
        <div className="sfh-roi-snapshot-strip" data-testid="sfh-quick-roi-snapshots">
          {snapshots.map((item) => (
            <div
              key={item.key}
              className={`sfh-roi-snapshot-card${item.positive ? " is-positive" : ""}`}
              data-testid={`sfh-roi-snap-${item.key}`}
            >
              <span>{item.label}</span>
              <strong>{item.value}</strong>
            </div>
          ))}
        </div>
      ) : (
        <div className="sfh-roi-grid" data-testid="sfh-quick-roi-body">
          <label className="sfh-cost-control">
            <span className="sfh-control-label">Cost Price</span>
            <div className="sfh-cost-shell">
              <span className="sfh-dollar">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={isKnownCost(cost) ? Number(cost) : ""}
                placeholder="—"
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "" || raw == null) {
                    setCost(null);
                    return;
                  }
                  const n = Number(raw);
                  if (!Number.isFinite(n)) {
                    setCost(null);
                    return;
                  }
                  setCost(Math.max(0, n));
                }}
                data-testid="sfh-cost-input"
              />
              <div className="sfh-stepper">
                <button type="button" onClick={() => step("up")} aria-label="Increase cost">
                  ▲
                </button>
                <button type="button" onClick={() => step("down")} aria-label="Decrease cost">
                  ▼
                </button>
              </div>
            </div>
            <small>Your purchase cost per unit</small>
          </label>

          <article className="sfh-result profit">
            <span>Est. Profit</span>
            <strong
              style={{
                color:
                  calc.profit == null ? "#172033" : calc.profit >= 0 ? "#078b63" : "#d64545",
              }}
            >
              {calc.profit == null ? "—" : money(calc.profit)}
            </strong>
          </article>

          <article className="sfh-result roi">
            <span>ROI</span>
            <strong
              style={{
                color: calc.roi != null && calc.roi >= 30 ? "#078b63" : "#172033",
              }}
            >
              {calc.roi == null ? "—" : pct(calc.roi)}
            </strong>
          </article>

          <article className="sfh-result">
            <span>Break-even</span>
            <strong>{calc.breakEven == null ? "—" : money(calc.breakEven)}</strong>
          </article>

          <article className="sfh-result">
            <span>Net Payout</span>
            <strong>{calc.netPayout == null ? "—" : money(calc.netPayout)}</strong>
          </article>
        </div>
      )}
    </section>
  );
}
